import React, { useState, useEffect, useRef, useCallback } from 'react';
import { IndianRupee, TrendingUp, Users, ShoppingBag, Wifi, WifiOff, CheckCircle2, Zap, RefreshCw, MessagesSquare, Megaphone, X, AlarmClock, Trophy } from 'lucide-react';
import { api, connectWS, inr, timeAgo, STATUS_STYLES } from '../api';
import AgentBoard from './AgentBoard';
import { toast } from 'sonner';
import AdminLeadsManager from './AdminLeadsManager';
import AdminChatPanel from './AdminChatPanel';
import AdminReport from './AdminReport';
import AdminTargets from './AdminTargets';
import WallOfFame from './WallOfFame';
import AdminAttendance from './AdminAttendance';
import AdminUsers from './AdminUsers';
import DeskAnalytics from './DeskAnalytics';
import AdminBulkOrders from './AdminBulkOrders';
import AdminOrdersModal from './AdminOrdersModal';

const IDLE_MINUTES = 10;

const KPI_STYLES = [
  { bg: '#FEF08A', border: '#FDE047', text: '#713F12', rotate: '-1deg' },
  { bg: '#FCE7F3', border: '#F9A8D4', text: '#831843', rotate: '0.8deg' },
  { bg: '#E0F2FE', border: '#7DD3FC', text: '#0C4A6E', rotate: '-0.6deg' },
  { bg: '#DCFCE7', border: '#86EFAC', text: '#14532D', rotate: '1deg' },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [feed, setFeed] = useState([]);
  const [weights, setWeights] = useState({});
  const [chatAgent, setChatAgent] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcastText, setBroadcastText] = useState('');
  const [, setTickNow] = useState(Date.now());
  const [syncStatus, setSyncStatus] = useState(null);
  const [viewAgent, setViewAgent] = useState(null);
  const [showOrders, setShowOrders] = useState(false);
  const wsRef = useRef(null);

  const loadUnread = useCallback(() => api.get('/chat-unread-counts').then((r) => setUnreadCounts(r.data)), []);

  const load = useCallback(() => {
    api.get('/admin/stats').then((r) => setStats(r.data));
    api.get('/woo/sync-status').then((r) => setSyncStatus(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    loadUnread();
    api.get('/settings/assignment').then((r) => setWeights(r.data.weights || {}));
    const ws = connectWS((msg) => {
      if (msg.type === 'leads-synced') {
        toast.info(`🔄 ${msg.imported} new lead(s) synced from store${msg.merged ? `, ${msg.merged} merged` : ''}`);
        load();
        return;
      }
      if (msg.type === 'chat') {
        window.dispatchEvent(new CustomEvent('snd-chat-admin', { detail: msg }));
        if (msg.message?.sender === 'agent') {
          toast(`💬 ${msg.message.sender_name}: ${msg.message.text?.slice(0, 60) || 'attachment'}`);
          loadUnread();
        }
        return;
      }
      if (msg.type === 'notification' && msg.kind === 'agent-offline') {
        toast.warning(`⚠️ ${msg.agent_name} went offline with ${msg.open_leads} open lead(s) — follow up!`, { duration: 10000 });
        setFeed((f) => [{ agent_id: msg.agent_id, action: `went OFFLINE with ${msg.open_leads} open leads ⚠️`, at: msg.at, key: Date.now() + Math.random() }, ...f].slice(0, 12));
        return;
      }
      if (msg.type === 'availability') {
        setStats((prev) => prev && ({
          ...prev,
          agents: prev.agents.map((a) => (a.id === msg.agent_id ? { ...a, available: msg.available } : a)),
        }));
        return;
      }
      if (msg.type === 'sale') {
        toast.success(`🎉 ${msg.agent_name} closed a sale of ${inr(msg.amount)} (${msg.customer_name})`);
        return;
      }
      if (msg.type === 'heartbeat') {
        setStats((prev) => prev && ({
          ...prev,
          agents: prev.agents.map((a) => (a.id === msg.agent_id ? { ...a, last_seen: msg.at, online: true } : a)),
        }));
        return;
      }
      if (msg.type === 'presence' || msg.type === 'activity') {
        setStats((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            agents: prev.agents.map((a) => {
              if (a.id !== msg.agent_id) return a;
              if (msg.type === 'presence') return { ...a, online: msg.online, last_seen: msg.at };
              return { ...a, last_action: msg.action, last_seen: msg.at, online: true };
            }),
          };
        });
        if (msg.type === 'activity') {
          setFeed((f) => [{ ...msg, key: Date.now() + Math.random() }, ...f].slice(0, 12));
        }
      }
    });
    wsRef.current = ws;
    const poll = setInterval(load, 30000);
    const tick = setInterval(() => setTickNow(Date.now()), 30000);
    return () => { ws.stop(); clearInterval(poll); clearInterval(tick); };
  }, [load]);

  if (!stats) return <div className="p-12 text-center text-slate-500 font-hand text-2xl">Loading dashboard…</div>;

  if (viewAgent) {
    return (
      <div data-testid="admin-agent-view">
        <div className="sticky top-16 z-40 bg-blue-600 text-white px-4 md:px-8 py-2 flex items-center gap-3">
          <button
            data-testid="back-to-admin-button"
            onClick={() => setViewAgent(null)}
            className="flex items-center gap-1 bg-white/20 hover:bg-white/30 rounded-full px-3 py-1 text-xs font-bold transition-colors">
            ← Back to Command Center
          </button>
          <p className="text-xs font-bold">
            👀 Viewing <span className="underline decoration-dotted">{viewAgent.name}</span>'s board as Admin — changes you make here are real
          </p>
        </div>
        <AgentBoard key={viewAgent.id} agent={viewAgent} sendActivity={() => {}} />
      </div>
    );
  }

  const setWeight = async (agentId, w) => {
    const next = { ...weights, [agentId]: Number(w) };
    setWeights(next);
    await api.put('/settings/assignment', { weights: next });
    const name = stats.agents.find((a) => a.id === agentId)?.name || 'Agent';
    toast.success(`${name} now gets ${w}x lead share`);
  };

  const kpis = [
    { label: 'Total Revenue', value: inr(stats.total_revenue), icon: IndianRupee, sub: `${stats.converted_leads} conversions` },
    { label: 'Conversion Rate', value: `${stats.conversion_rate}%`, icon: TrendingUp, sub: `${stats.converted_leads}/${stats.total_leads} leads` },
    { label: 'Total Leads', value: stats.total_leads, icon: Users, sub: `${stats.status_counts['Open'] || 0} open · ${stats.archived_count} archived` },
    { label: 'Orders Created', value: stats.orders_created, icon: ShoppingBag, sub: 'click to view & edit all orders', onClick: () => setShowOrders(true) },
  ];

  return (
    <main className="corkboard min-h-[calc(100vh-64px)] p-4 md:p-8 space-y-8" data-testid="admin-dashboard">
      <div className="flex items-baseline gap-3 flex-wrap">
        <h2 className="font-display font-extrabold text-2xl text-slate-900">Command Center</h2>
        <p className="font-hand text-xl text-slate-500">the whole team at a glance 📌</p>
        <div className="ml-auto flex items-center gap-2">
          {syncStatus?.last_sync_at ? (
            <span data-testid="sync-health-chip"
              title={`Last sync: fetched ${syncStatus.fetched ?? 0}, ${syncStatus.imported ?? 0} new, ${syncStatus.merged ?? 0} merged, ${syncStatus.skipped ?? 0} already in CRM${syncStatus.waiting ? `, ${syncStatus.waiting} pending <3min (next run)` : ''}${syncStatus.ok === false ? ` · LAST ATTEMPT FAILED: ${syncStatus.error}` : ''}`}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold border-2 ${syncStatus.ok === false ? 'bg-red-50 border-red-300 text-red-700' : 'bg-emerald-50 border-emerald-200 text-emerald-700'}`}>
              <span className={`w-2 h-2 rounded-full ${syncStatus.ok === false ? 'bg-red-500' : 'bg-emerald-500 animate-pulse'}`} />
              {syncStatus.ok === false ? 'Store sync failing' : `Store synced ${timeAgo(syncStatus.last_sync_at)}`}
            </span>
          ) : (
            <span data-testid="sync-health-chip" className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold border-2 bg-slate-50 border-slate-200 text-slate-400">
              <span className="w-2 h-2 rounded-full bg-slate-300" /> No sync yet
            </span>
          )}
          <button
            data-testid="crown-top-seller-button"
            onClick={async () => {
              try {
                const r = await api.post('/leaderboard/crown');
                toast.success(`👑 ${r.data.agent_name} crowned Top Seller of the Day (${inr(r.data.amount)}) — live on the TV board!`);
              } catch (e) {
                toast.error(e.response?.data?.detail || 'No sales to crown yet');
              }
            }}
            className="flex items-center gap-1.5 bg-yellow-400 text-yellow-950 rounded-full px-4 py-1.5 text-xs font-bold hover:bg-yellow-300 transition-colors"
          >
            <Trophy size={13} /> Crown Top Seller
          </button>
          <button
            data-testid="broadcast-button"
            onClick={() => setShowBroadcast(true)}
            className="flex items-center gap-1.5 bg-amber-500 text-white rounded-full px-4 py-1.5 text-xs font-bold hover:bg-amber-400 transition-colors"
          >
            <Megaphone size={13} /> Broadcast
          </button>
          <button
            data-testid="sync-woo-leads-button"
            onClick={async () => {
              const t = toast.loading('Pulling leads from staranddaisy.in…');
              try {
                const r = await api.post('/woo/sync-leads');
                toast.success(`Store sync: ${r.data.imported} new lead(s), ${r.data.merged} merged, ${r.data.skipped} already in CRM`, { id: t });
                load();
              } catch (e) {
                toast.error('Store sync failed — check connection', { id: t });
              }
            }}
            className="flex items-center gap-1.5 bg-violet-700 text-white rounded-full px-4 py-1.5 text-xs font-bold hover:bg-violet-600 transition-colors"
          >
            <RefreshCw size={13} /> Sync Store Leads
          </button>
          <button
            data-testid="simulate-incoming-lead-button"
            onClick={async () => {
              const r = await api.post('/leads/simulate-incoming');
              toast.success(`New Woo lead ${r.data.customer_name} → auto-assigned to ${r.data.assigned_to_name} (round-robin)`);
              load();
            }}
            className="flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-1.5 text-xs font-bold hover:bg-slate-700 transition-colors"
          >
            <Zap size={13} /> Simulate Incoming Lead
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6" data-testid="kpi-strip">
        {kpis.map((k, i) => {
          const s = KPI_STYLES[i];
          const Icon = k.icon;
          return (
            <div key={k.label} data-testid={`kpi-${k.label.toLowerCase().replace(/ /g, '-')}`}
              onClick={k.onClick}
              className={`sticky-shadow rounded-sm p-4 relative note-enter ${k.onClick ? 'cursor-pointer hover:-translate-y-1 transition-transform' : ''}`}
              style={{ background: s.bg, border: `1.5px solid ${s.border}`, transform: `rotate(${s.rotate})`, animationDelay: `${i * 80}ms` }}>
              <div className="tape" style={{ width: 60 }} />
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-bold uppercase tracking-wider opacity-70" style={{ color: s.text }}>{k.label}</p>
                <Icon size={15} style={{ color: s.text, opacity: 0.6 }} />
              </div>
              <p className="font-display font-extrabold text-2xl md:text-3xl mt-1" style={{ color: s.text }}>{k.value}</p>
              <p className="font-hand text-sm opacity-75 mt-0.5" style={{ color: s.text }}>{k.sub}</p>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          <h3 className="font-display font-bold text-lg text-slate-800 mb-3">Agents · Live Activity</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="agent-cards-grid">
            {stats.agents.map((a) => {
              const idleMin = a.online && a.last_seen ? Math.floor((Date.now() - new Date(a.last_seen).getTime()) / 60000) : 0;
              const isIdle = a.online && idleMin >= IDLE_MINUTES;
              return (
              <div key={a.id} data-testid={`admin-agent-card-${a.id}`}
                className={`bg-white border-2 rounded-xl p-4 sticky-shadow hover:-translate-y-0.5 transition-transform ${isIdle ? 'border-amber-400 ring-2 ring-amber-200' : 'border-slate-200'}`}>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-11 h-11 rounded-full flex items-center justify-center font-display font-extrabold text-sm text-slate-800 border-2 border-white shadow"
                      style={{ background: a.avatar_color }}>
                      {a.name.split(' ').map((w) => w[0]).join('')}
                    </div>
                    <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white ${isIdle ? 'bg-amber-400' : a.online ? 'bg-emerald-500 pulse-dot' : 'bg-slate-300'}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <button
                      data-testid={`view-agent-board-${a.id}`}
                      onClick={() => setViewAgent(a)}
                      title={`Open ${a.name}'s board`}
                      className="font-bold text-sm text-slate-900 truncate hover:text-blue-600 hover:underline decoration-dotted underline-offset-2 transition-colors text-left block max-w-full">
                      {a.name}
                    </button>
                    <p className={`text-[11px] font-semibold flex items-center gap-1 ${isIdle ? 'text-amber-600' : a.online ? 'text-emerald-600' : 'text-slate-400'}`} data-testid={`agent-status-${a.id}`}>
                      {isIdle ? <><AlarmClock size={10} /> Idle for {idleMin}m ⚠️</>
                        : a.online ? <><Wifi size={10} /> Online now</>
                        : <><WifiOff size={10} /> Last seen {timeAgo(a.last_seen)}</>}
                      {a.available === false && <span data-testid={`agent-paused-badge-${a.id}`} className="ml-1 text-[9px] font-bold bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full">⏸ NOT TAKING LEADS</span>}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-display font-extrabold text-base text-slate-900">{inr(a.revenue)}</p>
                    <p className="text-[10px] text-slate-400">revenue</p>
                  </div>
                  <button
                    data-testid={`message-agent-button-${a.id}`}
                    onClick={() => setChatAgent(a)}
                    title={`Message ${a.name}`}
                    className="relative shrink-0 p-2 rounded-full bg-purple-100 text-purple-700 hover:bg-purple-600 hover:text-white transition-colors"
                  >
                    <MessagesSquare size={14} />
                    {unreadCounts[a.id] > 0 && (
                      <span data-testid={`agent-unread-badge-${a.id}`} className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center animate-pulse">
                        {unreadCounts[a.id]}
                      </span>
                    )}
                  </button>
                </div>
                {a.last_action && (
                  <p className="font-hand text-sm text-slate-600 mt-2 bg-yellow-50 border border-yellow-200 rounded px-2 py-1 truncate" data-testid={`agent-last-action-${a.id}`}>
                    ✏️ {a.last_action}
                  </p>
                )}
                <div className="grid grid-cols-4 gap-1 mt-3 text-center">
                  {[['Leads', a.leads_assigned], ['Active', a.leads_open + a.leads_in_progress], ['Won', a.converted], ['To-dos', a.pending_reminders]].map(([l, v]) => (
                    <div key={l} className="bg-slate-50 rounded-lg py-1.5">
                      <p className="font-display font-extrabold text-sm text-slate-900">{v}</p>
                      <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">{l}</p>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-2.5 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
                  <span className="text-[11px] font-bold text-amber-800">Lead share (round-robin)</span>
                  <select
                    data-testid={`weight-select-${a.id}`}
                    value={weights[a.id] ?? 1}
                    onChange={(e) => setWeight(a.id, e.target.value)}
                    className="bg-white border border-amber-300 rounded-lg px-2 py-0.5 text-[11px] font-bold text-amber-900 focus:outline-none cursor-pointer"
                  >
                    <option value="0">0x — paused</option>
                    <option value="1">1x — normal</option>
                    <option value="2">2x — double</option>
                    <option value="3">3x — triple</option>
                  </select>
                </div>
              </div>
              );
            })}
          </div>

          <div className="mt-8">
            <AdminUsers />
          </div>

          <div className="mt-8">
            <DeskAnalytics />
          </div>

          <div className="mt-8">
            <AdminBulkOrders />
          </div>

          <div className="mt-8">
            <AdminLeadsManager agents={stats.agents} onChanged={load} />
          </div>

          <div className="mt-8">
            <AdminReport />
          </div>

          <div className="mt-8">
            <AdminTargets />
          </div>

          <div className="mt-8">
            <AdminAttendance />
          </div>

          <div className="mt-8">
            <WallOfFame />
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <h3 className="font-display font-bold text-lg text-slate-800 mb-3">Pipeline</h3>
            <div className="bg-white border-2 border-slate-200 rounded-xl p-4 sticky-shadow space-y-2" data-testid="pipeline-breakdown">
              {['Open', 'In-Progress', 'High-Priority', 'Closed'].map((s) => {
                const count = stats.status_counts[s] || 0;
                const pct = stats.total_leads ? (count / stats.total_leads) * 100 : 0;
                return (
                  <div key={s}>
                    <div className="flex justify-between text-[11px] font-semibold text-slate-600 mb-0.5">
                      <span className={`px-1.5 rounded border text-[10px] ${STATUS_STYLES[s]}`}>{s}</span>
                      <span>{count}</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${pct}%`, background: s === 'Closed' ? '#10B981' : s === 'High-Priority' ? '#EF4444' : s === 'In-Progress' ? '#8B5CF6' : '#3B82F6' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <h3 className="font-display font-bold text-lg text-slate-800 mb-3">Live Feed</h3>
            <div className="bg-white border-2 border-slate-200 rounded-xl p-3 sticky-shadow max-h-44 overflow-y-auto scroll-thin space-y-1.5" data-testid="live-activity-feed">
              {feed.length === 0 && <p className="font-hand text-base text-slate-400 text-center py-3">Waiting for agent activity…</p>}
              {feed.map((f) => {
                const ag = stats.agents.find((a) => a.id === f.agent_id);
                return (
                  <div key={f.key} className="flex items-center gap-2 text-[11px] text-slate-600">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
                    <span className="font-bold text-slate-800 shrink-0">{ag?.name.split(' ')[0] || 'Agent'}</span>
                    <span className="truncate">{f.action}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <h3 className="font-display font-bold text-lg text-slate-800 mb-3">Recent Orders</h3>
            <div className="bg-white border-2 border-slate-200 rounded-xl p-3 sticky-shadow space-y-1.5" data-testid="recent-orders-admin">
              {stats.recent_orders.map((o) => (
                <div key={o.id} className="flex items-center justify-between text-[11px] gap-2">
                  <div className="min-w-0">
                    <p className="font-bold text-slate-800 truncate">{o.customer_name}</p>
                    <p className="text-slate-400">by {o.created_by_name} · Woo #{o.woo_order_id}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-bold text-slate-900">{inr(o.total)}</p>
                    <p className="text-emerald-600 flex items-center gap-0.5 justify-end"><CheckCircle2 size={9} /> {o.sync_status}{o.mocked ? ' (mock)' : ''}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {chatAgent && <AdminChatPanel agent={chatAgent} onClose={() => setChatAgent(null)} onRead={loadUnread} />}
      {showOrders && <AdminOrdersModal onClose={() => setShowOrders(false)} onChanged={load} />}

      {showBroadcast && (
        <div className="fixed inset-0 z-[100] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowBroadcast(false)} data-testid="broadcast-overlay">
          <div className="bg-[#FFFBEB] border-2 border-amber-300 rounded-lg sticky-shadow w-full max-w-md relative rotate-[-0.4deg] p-4" onClick={(e) => e.stopPropagation()} data-testid="broadcast-modal">
            <div className="tape" />
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display font-bold text-lg text-slate-900 flex items-center gap-2"><Megaphone size={16} className="text-amber-600" /> Broadcast to all agents</h3>
              <button onClick={() => setShowBroadcast(false)} className="p-1.5 rounded-full hover:bg-amber-100 text-slate-500"><X size={16} /></button>
            </div>
            <p className="font-hand text-base text-amber-700 mb-2">this note lands on every agent's artboard at once 📣</p>
            <textarea
              data-testid="broadcast-text-input"
              value={broadcastText}
              onChange={(e) => setBroadcastText(e.target.value)}
              rows={3}
              placeholder="e.g. Team huddle at 5 PM — bring your conversion numbers!"
              className="w-full bg-white border border-amber-300 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
            />
            <button
              data-testid="broadcast-send-button"
              onClick={async () => {
                if (!broadcastText.trim()) return toast.error('Write a message first');
                const r = await api.post('/chat-broadcast', { text: broadcastText.trim() });
                toast.success(`📣 Broadcast pinned on ${r.data.sent} agent boards`);
                setBroadcastText('');
                setShowBroadcast(false);
              }}
              className="mt-2 w-full bg-amber-500 text-white rounded-lg py-2 text-sm font-bold hover:bg-amber-400 transition-colors"
            >
              Send to everyone
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
